import React, { createContext, useContext, useState } from "react";
import API from "../api";
import urls from "../api/urls";
import axios from "axios";

export const LockContext = createContext();

export const LockProvider = ({ children }) => {
  const [isLocked, setIsLocked] = useState({ is_locked: false });
  const [lockRules, setLockRules] = useState([]);
  const [expirySeconds, setExpirySeconds] = useState(0);
  const [lockedAwbNumberList, setLockedAwbNumberList] = useState([]);
  const [lockedAwbTotalCount, setLockedAwbTotalCount] = useState(0);

  // const demoCp = ["4967"];
  const { LOCK_ENGINE, GET_LOCKED_AWBS, VALIDATE_DOCUMENT_NUMBER } = urls;

  const getRemainingSeconds = (expiryString) => {
    const expiry = new Date(expiryString);
    const now = new Date();

    const diffMs = expiry - now;
    const diffSec = Math.floor(diffMs / 1000);

    return diffSec > 0 ? diffSec : 0;
  };

  const enrichPendingAwbs = async (pendingAwbs, spId = 4967) => {
    const enriched = await Promise.all(
      pendingAwbs.map(async (awb) => {
        const awbNo = awb.awbNumber?.toString() ?? "";

        // 11-digit → BOTH
        if (awbNo.length === 11) {
          return { ...awb, shipmentType: "BOTH" };
        }

        // 14-digit → validate with API
        if (awbNo.length === 14) {
          try {
            const res = await axios.get(
              `${VALIDATE_DOCUMENT_NUMBER}?bookingType=Cash&spId=${spId}&documentNumber=${awbNo}`,
              {
                // VERY IMPORTANT → prevents global error toast
                validateStatus: () => true,
              }
            );

            if (res.status === 400) {
              return { ...awb, shipmentType: "CREDIT" };
            }

            return { ...awb, shipmentType: "RETAIL" };
          } catch (err) {
            // fallback, should not hit here anymore
            return { ...awb, shipmentType: "CREDIT" };
          }
        }

        return { ...awb, shipmentType: "UNKNOWN" };
      })
    );

    return enriched;
  };

  const fetchAwbDetails = async (params = {}) => {
    try {
      const userinfo = JSON.parse(localStorage.getItem("userinfo"));
      const {
        page = 1,
        limit = 20,
        search = "",
        status = "",
        rule_id = "LOCK_NOT_BOOKED",
      } = params;

      const queryParams = {
        cp_id: userinfo.cpId,
        page,
        limit,
        rule_id,
      };

      if (search) {
        queryParams.search = search;
      }

      if (status) {
        queryParams.status = status;
      }

      const res = await API.Api(GET_LOCKED_AWBS).getwithParams(queryParams);

      const awbs =
        res?.response?.data?.data?.pendingAwbs ||
        res?.response?.data?.data ||
        [];
      const enrichedAwbs = await enrichPendingAwbs(awbs, userinfo.cpId);
      const totalCount =
        res?.response?.data?.data?.totalCount ||
        res?.response?.data?.pagination?.totalCount ||
        awbs.length;

      setLockedAwbNumberList(enrichedAwbs);
      setLockedAwbTotalCount(totalCount);
    } catch (err) {
      console.error("Error fetching AWBs:", err);
    }
  };

  /* eslint-disable */
  // Helper to get redirect URL
  const getRedirectUrl = (ruleId) => {
    const notBookedUrl = "https://bookings.innofulfill.com/not-booked-shipment";
    const redirectUrl = "/locked-shipments";
    switch (ruleId) {
      case "LOCK_NOT_BOOKED":
        return notBookedUrl;
      default:
        return `${redirectUrl}?rule_id=${ruleId}`;
    }
  };

  const fetchLockStatus = async (cpId) => {
    try {
      if (!cpId) {
        let userinfo = JSON.parse(localStorage.getItem("userinfo"));
        if (!userinfo?.cpId) {
          return;
        }
        cpId = userinfo?.cpId;
      }

      const url = `${LOCK_ENGINE}?cp_id=${cpId}`;
      const res = await API.Api(url).get();

      const rules = res?.response?.data?.rules || [];

      // get LOCK_NOT_BOOKED rule
      let lockRule = rules.find((r) => r.rule_id === "LOCK_NOT_BOOKED");
      if (!lockRule) {
        lockRule = {
          rule_id: "LOCK_NOT_BOOKED",
          nearest_expiry: null,
          is_locked: false,
          count: 0,
          awbs: [],
        };
      }

      lockRule.redirectUrl = getRedirectUrl(lockRule.rule_id);

      // expiry only if value is not null
      if (lockRule?.nearest_expiry) {
        setExpirySeconds(getRemainingSeconds(lockRule.nearest_expiry));
      }

      lockRule.count = (lockRule?.count || lockRule?.total_count) ?? 0;

      // set green bg if count = 0
      if (lockRule?.count == 0) {
        lockRule.bgColor = "rgba(0,150,0,0.15)";
      }

      setIsLocked(lockRule);

      // 1️⃣ Add expirySeconds + cap rule_id to 14 chars
      const enhancedRules = rules.map((r) => ({
        ...r,
        redirectUrl: getRedirectUrl(r.rule_id),

        // Cap rule_id to max 14 characters
        rule_id: r?.rule_id
          ? String(r.rule_id).slice(0, 18) + ".."
          : r?.rule_id,
        ...((r?.count || r?.total_count) === 0
          ? { bgColor: "rgba(0, 150, 0, 0.15)" }
          : {}),
        expirySeconds: r?.nearest_expiry
          ? getRemainingSeconds(r?.nearest_expiry)
          : null,
        count: (r?.count || r?.total_count) ?? 0,
        awbs: r?.pending_awbs,
      }));

      // 2️⃣ Remove LOCK_NOT_BOOKED because you will push enhanced lockRule separately
      const filteredRules = enhancedRules.filter((r) => {
        return r.rule_id !== "LOCK_NOT_BOOKED..";
      });

      // 3️⃣ Finally append lockRule at the end
      const finalRules = [
        {
          ...lockRule,
          onClickRefresh: () => fetchLockStatus(),
        },
        ...filteredRules,
      ];

      // 4️⃣ update state
      setLockRules(finalRules.splice(0, 9));
    } catch (err) {
      console.log("LOCK API ERROR:", err);
    }
  };

  return (
    <LockContext.Provider
      value={{
        isLocked,
        expirySeconds,
        fetchLockStatus,
        lockRules,
        lockedAwbNumberList,
        setLockedAwbNumberList,
        lockedAwbTotalCount,
        fetchAwbDetails,
      }}
    >
      {children}
    </LockContext.Provider>
  );
};

export const useLock = () => useContext(LockContext);
